import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MessagesSquare, Wand2, FileUp, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import VoicePreviewButton from "@/components/VoicePreviewButton";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const EMOTIONS = ["neutre", "calme", "colère", "murmure", "joie", "tristesse", "peur"];
const EMOTION_COLOR = { neutre: "text-muted-foreground border-border", calme: "text-sky-300 border-sky-400/40", "colère": "text-red-400 border-red-400/40", murmure: "text-violet-300 border-violet-400/40", joie: "text-amber-300 border-amber-400/40", tristesse: "text-blue-300 border-blue-400/40", peur: "text-fuchsia-300 border-fuchsia-400/40" };

const EXAMPLE = `Kael (calme): Le portail est ouvert. Tu es prêt ?
Lyra (joie): Prête depuis toujours. Allons-y.
Kael (murmure): Reste derrière moi. Quoi qu'il arrive.`;

// Parse "Name (emotion): line" text; lines without a name go to the previous character or "Narrateur".
export function parseScript(text) {
  const out = [];
  let last = "Narrateur";
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^([^:()[\]]{1,40})(?:\s*[([]\s*([^)\]]{1,20})\s*[)\]])?:\s*(.+)$/);
    if (m) {
      last = m[1].trim();
      const emo = (m[2] || "").trim().toLowerCase();
      out.push({ character: last, text: m[3].trim(), emotion: EMOTIONS.includes(emo) ? emo : null });
    } else out.push({ character: last, text: line, emotion: null });
  }
  return out;
}

// Convert a Fountain screenplay (CHARACTER in caps, optional (parenthetical), dialogue lines) into "Name (emotion): text".
export function fountainToScript(src) {
  const lines = src.replace(/\r/g, "").split("\n");
  const out = [];
  let char = null, emo = null;
  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    if (!t) { char = null; emo = null; continue; }
    if (/^(INT\.|EXT\.|INT\/EXT|EST\.|I\/E)/i.test(t) || t.startsWith("!") || t.startsWith("=") || t.startsWith("#") || t.startsWith("[[")) { char = null; continue; }
    if (!char && /^@?[A-ZÀ-Ý0-9 .'\-]{1,40}(\s*\(.*\))?\s*\^?$/.test(t) && t !== t.toLowerCase() && lines[i + 1] && lines[i + 1].trim()) {
      char = t.replace(/^@/, "").replace(/\s*\(.*\)\s*\^?$/, "").replace(/\^$/, "").trim();
      const cap = char.charAt(0) + char.slice(1).toLowerCase();
      char = cap; emo = null;
      continue;
    }
    if (char && /^\(.*\)$/.test(t)) { const e = t.slice(1, -1).trim().toLowerCase(); emo = EMOTIONS.includes(e) ? e : emo; continue; }
    if (char) out.push(`${char}${emo ? ` (${emo})` : ""}: ${t}`);
  }
  return out.join("\n");
}

export default function DialogueComposer({ useEleven, elVoices, falVoices, projectId, onCreated }) {
  const [title, setTitle] = useState("Scène 1");
  const [script, setScript] = useState("");
  const [voiceMap, setVoiceMap] = useState({});
  const [emotionOverride, setEmotionOverride] = useState({});
  const [loading, setLoading] = useState(false);
  const [defaults, setDefaults] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/voice/characters").then(({ data }) => setDefaults(data)).catch(() => {}); }, []);
  const provider = useEleven ? "elevenlabs" : "fal";
  const defaultFor = (ch) => defaults.find((d) => d.name === ch.trim().toLowerCase());

  const lines = useMemo(() => parseScript(script).map((l, i) => ({ ...l, emotion: emotionOverride[i] || l.emotion || defaultFor(l.character)?.emotion || "neutre" })), [script, emotionOverride, defaults]);
  const characters = useMemo(() => [...new Set(lines.map((l) => l.character))], [lines]);
  const options = useEleven ? elVoices.map((v) => ({ id: v.voice_id, label: `${v.favorite ? "★ " : ""}${v.name.split(" - ")[0]}` })) : falVoices.map((v) => ({ id: v, label: v }));
  const voiceFor = (ch, i) => {
    if (voiceMap[ch]) return voiceMap[ch];
    const d = defaultFor(ch);
    const remembered = d && d.provider === provider ? (useEleven ? d.voice_id : d.voice) : null;
    if (remembered && options.some((o) => o.id === remembered)) return remembered;
    return options[i % Math.max(1, options.length)]?.id || "";
  };

  const importFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const isFountain = /\.fountain$/i.test(file.name) || /\n[A-Z][A-Z .'\-]{1,39}\n[^\n]/.test(text) && !/^[^:\n]{1,40}:\s*\S/m.test(text);
    const converted = isFountain ? fountainToScript(text) : text.trim();
    if (!converted) { toast.error("Aucune réplique trouvée dans ce fichier."); return; }
    setScript(converted);
    setEmotionOverride({});
    setTitle(file.name.replace(/\.(txt|fountain)$/i, "").slice(0, 80) || title);
    toast.success(`Script importé — ${parseScript(converted).length} répliques (${isFountain ? "Fountain" : "texte"}).`);
  };
  const cost = lines.length * 2;

  const generate = async () => {
    if (lines.length === 0) { toast.error("Écrivez au moins une réplique (Personnage: texte)."); return; }
    setLoading(true);
    try {
      const payload = {
        title, provider: useEleven ? "elevenlabs" : "fal", project_id: projectId,
        lines: lines.map((l) => {
          const vid = voiceFor(l.character, characters.indexOf(l.character));
          const label = (options.find((o) => o.id === vid)?.label || vid).replace("★ ", "");
          return { character: l.character, text: l.text, voice: label, voice_id: useEleven ? vid : null, emotion: l.emotion };
        }),
      };
      const { data } = await api.post("/voice/dialogue", payload);
      toast.success(`Dialogue lancé — ${data.lines.length} répliques en cours (${data.credits_used} cr). Voix mémorisées par personnage.`);
      api.get("/voice/characters").then(({ data: d }) => setDefaults(d)).catch(() => {});
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="dialogue-composer">
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label>Titre de la scène</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} className="bg-background border-border" data-testid="dialogue-title" />
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => fileRef.current?.click()} className="text-xs px-2.5 py-1.5 rounded-full border border-primary/40 text-primary hover:bg-primary/15 transition-colors inline-flex items-center gap-1.5" data-testid="dialogue-import">
            <FileUp size={12} /> Importer .txt / .fountain
          </button>
          <input ref={fileRef} type="file" accept=".txt,.fountain,text/plain" className="hidden" onChange={(e) => { importFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="dialogue-import-input" />
          <button type="button" onClick={() => setScript(EXAMPLE)} className="text-xs px-2.5 py-1.5 rounded-full border border-secondary/40 text-secondary hover:bg-secondary/15 transition-colors" data-testid="dialogue-example">
            Charger un exemple
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Script — une réplique par ligne, format <span className="font-mono text-primary">Personnage (émotion): texte</span> · émotions : {EMOTIONS.join(", ")}</Label>
        <Textarea value={script} onChange={(e) => setScript(e.target.value)} placeholder={EXAMPLE} className="min-h-40 bg-background border-border font-mono text-sm resize-none" data-testid="dialogue-script" />
      </div>

      {characters.length > 0 && (
        <div className="space-y-2" data-testid="dialogue-cast">
          <Label>Distribution — {characters.length} personnage{characters.length > 1 ? "s" : ""}, {lines.length} réplique{lines.length > 1 ? "s" : ""}</Label>
          <div className="grid sm:grid-cols-2 gap-2">
            {characters.map((ch, i) => {
              const vid = voiceFor(ch, i);
              return (
                <div key={ch} className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2" data-testid={`dialogue-cast-${i}`}>
                  <span className="text-sm font-semibold w-24 truncate flex items-center gap-1.5" title={defaultFor(ch) ? `Voix mémorisée : ${defaultFor(ch).voice}` : ""}>
                    {defaultFor(ch) && !voiceMap[ch] ? <UserCheck size={13} className="text-primary" data-testid={`dialogue-cast-remembered-${i}`} /> : <MessagesSquare size={13} className="text-secondary" />} {ch}
                  </span>
                  <Select value={vid} onValueChange={(v) => setVoiceMap((m) => ({ ...m, [ch]: v }))}>
                    <SelectTrigger className="bg-card border-border h-8 text-xs flex-1" data-testid={`dialogue-voice-${i}`}><SelectValue placeholder="Voix" /></SelectTrigger>
                    <SelectContent className="glass border-border max-h-64">
                      {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {useEleven && <VoicePreviewButton voiceId={vid} className="h-8 w-8" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {lines.length > 0 && (
        <div className="space-y-1.5" data-testid="dialogue-lines">
          <Label>Répliques & émotions <span className="text-muted-foreground font-normal">— l'émotion règle stabilité, style et vitesse de la voix</span></Label>
          <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-xs rounded-lg bg-background border border-border px-2.5 py-1.5" data-testid={`dialogue-line-${i}`}>
                <span className="font-mono text-muted-foreground w-5">{i + 1}.</span>
                <span className="font-semibold w-20 truncate">{l.character}</span>
                <span className="flex-1 truncate text-foreground/85">{l.text}</span>
                <Select value={l.emotion} onValueChange={(v) => setEmotionOverride((m) => ({ ...m, [i]: v }))}>
                  <SelectTrigger className={`h-7 w-28 text-[11px] bg-card ${EMOTION_COLOR[l.emotion] || ""}`} data-testid={`dialogue-emotion-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="glass border-border">
                    {EMOTIONS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button onClick={generate} disabled={loading || lines.length === 0} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 glow-primary gap-2" data-testid="dialogue-generate">
        {loading ? <Loader2 className="animate-spin" size={16} /> : <Wand2 size={16} />} Générer le dialogue · {cost} cr
      </Button>
    </div>
  );
}
